/**
 * B7 · Referral record on the profile. READ-ONLY for the user.
 *
 * COPY DISCIPLINE: an unspent referral is shown as a COUNT of discounts, with
 * the terms spelled out ("10% off a card fee"). It is never shown as an amount
 * of money the user has, because it is not — it is an entitlement resolved at
 * checkout and it never accrues anywhere.
 */
import { useEffect, useState } from "react";
import { SectionTitle } from "@/components/consumer/chrome";
import { getMyReferralSummary, describeReward, type ReferralSummary } from "@/services/referrals";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function ReferralRecord() {
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      const { data, error } = await getMyReferralSummary();
      if (!live) return;
      if (error) setFailed(true);
      setSummary(data);
    })();
    return () => {
      live = false;
    };
  }, []);

  if (failed || !summary) return null;
  if (!summary.code && summary.referrals_made === 0) return null;

  return (
    <div className="mb-3" data-testid="s9-referral-record">
      <SectionTitle className="mb-3">Your invitations</SectionTitle>

      <div className="mb-2.5 grid grid-cols-3 gap-2.5">
        <div className="flex flex-col gap-1 rounded-2xl border border-foreground/10 bg-white p-[18px]">
          <span
            className="font-brand text-[28px] font-semibold leading-none tracking-[-0.02em]"
            data-testid="s9-referrals-ordered"
          >
            {summary.referrals_made}
          </span>
          <span className="text-[13px] font-medium text-slate-muted">Friends who ordered</span>
        </div>
        <div className="flex flex-col gap-1 rounded-2xl border border-foreground/10 bg-white p-[18px]">
          <span
            className="font-brand text-[28px] font-semibold leading-none tracking-[-0.02em]"
            data-testid="s9-discounts-unspent"
          >
            {summary.discounts_unspent}
          </span>
          <span className="text-[13px] font-medium text-slate-muted">Discounts ready to use</span>
        </div>
        <div className="flex flex-col gap-1 rounded-2xl border border-foreground/10 bg-white p-[18px]">
          <span
            className="font-brand text-[28px] font-semibold leading-none tracking-[-0.02em]"
            data-testid="s9-discounts-used"
          >
            {summary.discounts_used}
          </span>
          <span className="text-[13px] font-medium text-slate-muted">Discounts used</span>
        </div>
      </div>

      <p className="m-0 mb-3 text-[13.5px] leading-[1.5] text-foreground/[0.78]">
        One discount applies to each card you order — it comes off the card fee at checkout, automatically.
      </p>

      {summary.invitations.length > 0 ? (
        <ul className="m-0 list-none space-y-2 p-0" data-testid="s9-invitation-log">
          {summary.invitations.map((inv) => (
            <li
              key={inv.event_id}
              className="rounded-2xl border border-foreground/10 bg-white px-4 py-3 text-[14px] leading-[1.5]"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold text-ink">{inv.invitee_name || "A friend"}</span>
                <span className="text-slate-muted">ordered {fmtDate(inv.earned_at)}</span>
                <span className="flex-1" />
                <span
                  className={
                    inv.consumed_at
                      ? "text-[13px] font-semibold text-slate-muted"
                      : "text-[13px] font-semibold text-leaf"
                  }
                >
                  {inv.consumed_at ? `Used ${fmtDate(inv.consumed_at)}` : "Ready to use"}
                </span>
              </div>
              <div className="mt-0.5 text-[13.5px] text-foreground/[0.78]">{describeReward(inv.reward)}</div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
